import { useCallback, useState } from 'react';
import { X, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { motion } from "framer-motion";
import { LoadingSpinner } from '../LoadingSpinner'; // Assuming you have a LoadingSpinner component

interface MappingStats {
  total: number;
  valid: number;
  invalid: number;
  matched: number;
  unmatched: number;
}

interface AIMappingProgressDialogProps {
  isOpen: boolean;
  progress: number; // Percentage from 0 to 100
  onClose: () => void;
  onCancel?: () => void; // Optional callback for cancellation
  onRetry?: () => void; // Optional callback for retry
  error?: string; // Optional error message
  isComplete?: boolean; // Whether the operation is complete
  stats?: MappingStats; // Optional mapping statistics
  hasPartialSuccess?: boolean; // Whether there was partial success
  progressDetail?: {
    current: number;
    total: number;
    message?: string;
  }; // Detailed progress information
}

/**
 * Dialog component that shows the progress of AI mapping operations
 * Enhanced to handle partial success scenarios and provide retry functionality
 */
export default function AIMappingProgressDialog({
  isOpen,
  progress,
  onClose,
  onCancel,
  onRetry,
  error,
  isComplete = false,
  stats,
  hasPartialSuccess = false,
  progressDetail,
}: AIMappingProgressDialogProps) {
  const [isCancelling, setIsCancelling] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  
  const handleDirectClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleRetry = useCallback(() => {
    if (onRetry) {
      setIsRetrying(true);
      onRetry();
      // Reset isRetrying after a short delay to show the spinner briefly
      setTimeout(() => setIsRetrying(false), 1000);
    }
  }, [onRetry]);

  if (!isOpen) {
    return null;
  }

  // Determine the status message and color based on the current state
  let statusMessage = 'Generating AI mapping suggestions...';
  let statusColor = 'text-gray-700 dark:text-gray-300';
  let progressBarColor = 'bg-blue-600';
  let detailMessage = '';
  
  // Add more detailed status message based on progress
  if (!error && !isComplete && progress > 0 && progress < 100) {
    if (progressDetail) {
      detailMessage = progressDetail.message ||
        `${progressDetail.current}/${progressDetail.total} properties processed`;
    } else {
      detailMessage = `Processing property mappings...`;
    }
  } else if (progress === 100 && !isComplete) {
    detailMessage = 'Finalizing results...';
  }
  
  // Handle partial success case with more informative message
  if (isComplete && hasPartialSuccess && stats) {
    const successRate = stats.matched / stats.total * 100;
    detailMessage = `Successfully mapped ${stats.matched} of ${stats.total} properties (${Math.round(successRate)}%)`;
  }
  
  // Handle completion case with full stats
  if (isComplete && stats && !hasPartialSuccess) {
    const successRate = stats.matched / stats.total * 100;
    detailMessage = `Successfully mapped ${stats.matched} of ${stats.total} properties (${Math.round(successRate)}%)`;
  }

  if (error) {
    statusMessage = 'Error occurred';
    statusColor = 'text-red-600 dark:text-red-400';
    progressBarColor = 'bg-red-500';
  } else if (isComplete && hasPartialSuccess) {
    statusMessage = 'AI mapping completed with partial success';
    statusColor = 'text-yellow-600 dark:text-yellow-400';
    progressBarColor = 'bg-yellow-500';
  } else if (isComplete) {
    statusMessage = 'AI mapping completed successfully!';
    statusColor = 'text-green-600 dark:text-green-400';
    progressBarColor = 'bg-green-500';
  }

  return (
    <motion.div
      className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[480px] mx-4 border border-gray-200 dark:border-gray-700"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
      >
        <div className="px-6 pt-5 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isComplete ? 'AI Mapping Complete' : 'AI Mapping in Progress'}
            </h2>
            <button
              onClick={handleDirectClose}
              className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="space-y-4">
            <div className="text-center">
              {error ? (
                <div className="flex flex-col items-center">
                  <AlertTriangle className="h-8 w-8 text-red-500 mb-2" />
                  <p className={`font-medium mb-2 ${statusColor}`}>
                    {statusMessage}
                  </p>
                  <p className="text-sm text-red-500 dark:text-red-300">
                    {error}
                  </p>
                </div>
              ) : isComplete ? (
                <div className="flex flex-col items-center">
                  {hasPartialSuccess ? (
                    <AlertTriangle className="h-8 w-8 text-yellow-500 mb-2" />
                  ) : (
                    <CheckCircle className="h-8 w-8 text-green-500 mb-2" />
                  )}
                  <p className={`font-medium ${statusColor}`}>
                    {statusMessage}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <LoadingSpinner size="md" className="text-blue-500 mb-2" />
                  <p className={`font-medium ${statusColor}`}>
                    {statusMessage}
                  </p>
                  {detailMessage && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {detailMessage}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <motion.div
                className={`h-2.5 rounded-full ${progressBarColor}`}
                initial={{ width: '0%' }}
                animate={{ width: `${Math.max(1, progress)}%` }}
                transition={{
                  type: "spring",
                  stiffness: 60,
                  damping: 15,
                  duration: 0.3
                }}
              ></motion.div>
            </div>
            <div className="flex justify-between items-center mt-1">
              <p className="text-gray-700 dark:text-gray-300 text-center font-medium w-full">
                {Math.round(progress)}% Complete
                {progressDetail && !isComplete && !error && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                    ({progressDetail.current}/{progressDetail.total})
                  </span>
                )}
              </p>

            </div>

            {/* Stats display when complete or partial success */}
            {stats && (isComplete || hasPartialSuccess || (error && stats.valid > 0)) && (
              <div className="mt-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-md">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Mapping Results:</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Total properties:</span>
                    <span className="font-medium">{stats.total}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Valid mappings:</span>
                    <span className="font-medium text-green-600 dark:text-green-400">{stats.valid}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Invalid mappings:</span>
                    <span className="font-medium text-red-600 dark:text-red-400">{stats.invalid}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Matched properties:</span>
                    <span className="font-medium text-blue-600 dark:text-blue-400">{stats.matched}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Unmatched properties:</span>
                    <span className="font-medium text-yellow-600 dark:text-yellow-400">{stats.unmatched}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 rounded-b-lg border-t border-gray-200 dark:border-gray-800">
          <div className="flex justify-end gap-3">
            {/* Retry button - only show when there's an error or partial success and onRetry is provided */}
            {onRetry && (error || hasPartialSuccess || (stats && stats.invalid > 0)) && (
              <button
                onClick={handleRetry}
                className="px-4 py-2 text-sm font-medium rounded-md inline-flex items-center gap-2 bg-yellow-600 text-white hover:bg-yellow-700"
              >
                {isRetrying ? (
                  <LoadingSpinner size="sm" className="text-white" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span>Retry Failed</span>
              </button>
            )}
            
            {/* Close/Cancel button */}
            <button
              onClick={() => {
                if (error || isComplete || progress >= 100) {
                  handleDirectClose();
                } else {
                  setIsCancelling(true);
                  if (onCancel) {
                    onCancel();
                    // Reset isCancelling after a short delay to show the spinner briefly
                    setTimeout(() => setIsCancelling(false), 1000);
                  } else {
                    handleDirectClose();
                  }
                }
              }}
              className={`
                px-4 py-2 text-sm font-medium rounded-md inline-flex items-center gap-2
                ${
                  error || isComplete || progress >= 100
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }
              `}
            >
              {error || isComplete || progress >= 100 ? (
                'Close'
              ) : (
                <>
                  {isCancelling && <LoadingSpinner size="sm" className="text-white" />}
                  <span>Cancel</span>
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
